import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../components/AuthContext";
import ConfigContext from "../../components/ConfigContext";
import { ReactBundleModel } from "../Models/ReactBundleModel";
import { fetchReactArtifact, ReactArtifactAuthenticationError } from "./ReactArtifactFetch";
import {
  ensureReactRuntimeIconModules,
  REACT_DOM_VERSION_RANGE,
  REACT_LIBRARY_VERSIONS,
  REACT_RUNTIME_API_VERSION,
  REACT_VERSION_RANGE,
  registerReactRuntimeBridge,
  SUPPORTED_REACT_COMPONENT_SDK_VERSIONS,
} from "./ReactRuntimeBridge";

type ArtifactEnvironment = { fetch?: (url: RequestInfo, init?: RequestInit) => Promise<Response>; root?: HTMLElement; instanceId?: string; manifest?: unknown };

type ReactModule = Record<string, React.ComponentType<any>>;
type LoadedReactBuild = { module: ReactModule; manifest: ReactBundleManifest };
type ReactBundleManifest = {
  format: "randol-react-bundle";
  formatVersion: number;
  sourceHash: string;
  runtimeApiVersion: string;
  runtime: {
    reactVersionRange: string;
    reactDomVersionRange: string;
    sdkVersion: string;
    libraries?: Array<{ packageName: string; version: string }>;
    iconModules?: string[];
  };
  module: string;
  stylesheet?: string;
  components: Array<{ id: string; name: string; exportName: string }>;
};

export interface IReactBundleContext {
  buildId?: string;
  componentStatus: (componentName?: string, componentId?: string) => "loading" | "bound" | "unbound";
  loadComponent: (componentName?: string, componentId?: string) => Promise<{ default: React.ComponentType<any> }>;
}

const moduleByBuildId = new Map<string, Promise<LoadedReactBuild>>();
const manifestByBuildId = new Map<string, Promise<ReactBundleManifest>>();
const stylesheetByUrl = new Map<string, Promise<void>>();
const absoluteHttpUrlRegex = /^https?:\/\//i;
const artifactNameRegex = /^[a-zA-Z0-9._-]+$/;
const manifestArtifactName = "bundle.manifest.json";
const supportedBundleFormatVersions = [1, 2] as const;
const maximumCachedBuilds = 10;

const ReactBundleContext = createContext<IReactBundleContext>({
  componentStatus: () => "unbound",
  loadComponent: async (componentName) => {
    throw new Error(`React component '${componentName}' cannot be loaded because this App has no React bundle.`);
  },
});

function setBounded<TKey, TValue>(cache: Map<TKey, TValue>, key: TKey, value: TValue): void {
  cache.set(key, value);
  while (cache.size > maximumCachedBuilds) {
    const oldestKey = cache.keys().next().value as TKey | undefined;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }
}

function removeInactiveStylesheets(activeUrl?: string, root: ParentNode = document): void {
  root.querySelectorAll<HTMLLinkElement>("link[data-randol-react-stylesheet]").forEach((link) => {
    const stylesheetUrl = link.dataset.randolReactStylesheet;
    if (stylesheetUrl !== activeUrl) {
      link.remove();
      if (stylesheetUrl) stylesheetByUrl.delete(link.dataset.randolReactStyleCacheKey ?? stylesheetUrl);
    }
  });
}

function loadStylesheet(
  url: string,
  getAccessToken: () => Promise<string | undefined>,
  designerOrigin?: string,
  environment?: ArtifactEnvironment
): Promise<void> {
  const cacheKey = environment?.instanceId ? `${environment.instanceId}:${url}` : url;
  const cached = stylesheetByUrl.get(cacheKey);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existing = Array.from((environment?.root ?? document).querySelectorAll<HTMLLinkElement>("link[data-randol-react-stylesheet]")).find(
      (link) => link.dataset.randolReactStylesheet === url
    );
    if (existing) {
      resolve();
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    fetchReactArtifact(url, getAccessToken, designerOrigin, environment?.fetch)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.onload = () => {
          URL.revokeObjectURL(blobUrl);
          resolve();
        };
        link.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          reject(new Error(`Could not load React bundle stylesheet '${url}'.`));
        };
        removeInactiveStylesheets(url, environment?.root);
        (environment?.root ?? document.head).appendChild(link);
      })
      .catch(reject);
    link.dataset.randolReactStylesheet = url;
    link.dataset.randolReactStyleCacheKey = cacheKey;
  });
  setBounded(stylesheetByUrl, cacheKey, promise);
  promise.catch(() => stylesheetByUrl.delete(cacheKey));
  return promise;
}

function findDescriptor(manifest: ReactBundleManifest, buildId: string, componentName?: string, componentId?: string) {
  const descriptor = componentName
    ? manifest.components.find((component) => component.name === componentName)
    : manifest.components.find((component) => component.id.toLowerCase() === componentId?.toLowerCase());
  if (!descriptor) {
    const reference = componentName ?? componentId ?? "unassigned";
    throw new Error(`React component '${reference}' is not present in build '${buildId}'.`);
  }

  return descriptor;
}

function resolveArtifactUrl(apiBaseUrl: string, artifactUrl: string): string {
  if (absoluteHttpUrlRegex.test(artifactUrl)) {
    return artifactUrl;
  }

  const normalizedBaseUrl = `${apiBaseUrl.replace(/\/+$/, "")}/`;
  return new URL(artifactUrl.replace(/^\/+/, ""), normalizedBaseUrl).toString();
}

function getArtifactUrl(apiBaseUrl: string, definitionId: string, buildId: string, artifactName: string): string {
  if (!artifactNameRegex.test(artifactName)) {
    throw new Error(`React build '${buildId}' contains invalid artifact name '${artifactName}'.`);
  }

  return resolveArtifactUrl(apiBaseUrl, `ReactBuildArtifact/${definitionId}/${buildId}/${artifactName}`);
}

function validateManifest(definition: ReactBundleModel, manifest: ReactBundleManifest): void {
  if (
    manifest.format !== "randol-react-bundle" ||
    !supportedBundleFormatVersions.some((version) => version === manifest.formatVersion) ||
    manifest.runtimeApiVersion !== REACT_RUNTIME_API_VERSION ||
    manifest.runtime?.reactVersionRange !== REACT_VERSION_RANGE ||
    manifest.runtime?.reactDomVersionRange !== REACT_DOM_VERSION_RANGE ||
    !SUPPORTED_REACT_COMPONENT_SDK_VERSIONS.some((version) => version === manifest.runtime?.sdkVersion)
  ) {
    throw new Error(`React build '${definition.BuildId}' manifest does not match the published bundle reference.`);
  }

  const unsupportedLibrary = manifest.runtime.libraries?.find(
    (library) => REACT_LIBRARY_VERSIONS[library.packageName as keyof typeof REACT_LIBRARY_VERSIONS] !== library.version
  );
  if (unsupportedLibrary) {
    throw new Error(
      `React build '${definition.BuildId}' requires unsupported library '${unsupportedLibrary.packageName}' version '${unsupportedLibrary.version}'.`
    );
  }

  if (manifest.formatVersion === 2 && !Array.isArray(manifest.runtime.iconModules)) {
    throw new Error(`React build '${definition.BuildId}' manifest is missing its icon module list.`);
  }

  if (!manifest.components?.length) {
    throw new Error(`React build '${definition.BuildId}' manifest has no component descriptors.`);
  }
}

function loadManifest(
  definition: ReactBundleModel,
  definitionId: string,
  apiBaseUrl: string,
  getAccessToken: () => Promise<string | undefined>,
  designerOrigin?: string,
  environment?: ArtifactEnvironment
): Promise<ReactBundleManifest> {
  const cacheKey = `${environment?.instanceId ?? "standalone"}:${apiBaseUrl}:${definitionId}:${definition.BuildId}`;
  const cached = manifestByBuildId.get(cacheKey);
  if (cached) return cached;

  const promise = (async () => {
    const manifestUrl = getArtifactUrl(apiBaseUrl, definitionId, definition.BuildId, manifestArtifactName);
    const manifest = (environment?.manifest ??
      (await (await fetchReactArtifact(manifestUrl, getAccessToken, designerOrigin, environment?.fetch)).json())) as ReactBundleManifest;
    validateManifest(definition, manifest);
    return manifest;
  })();
  setBounded(manifestByBuildId, cacheKey, promise);
  promise.catch(() => manifestByBuildId.delete(cacheKey));
  return promise;
}

function loadModule(
  definition: ReactBundleModel,
  definitionId: string,
  apiBaseUrl: string,
  getAccessToken: () => Promise<string | undefined>,
  designerOrigin?: string,
  environment?: ArtifactEnvironment
): Promise<LoadedReactBuild> {
  const cacheKey = `${environment?.instanceId ?? "standalone"}:${apiBaseUrl}:${definitionId}:${definition.BuildId}`;
  const cached = moduleByBuildId.get(cacheKey);
  if (cached) {
    return cached;
  }

  registerReactRuntimeBridge();
  const promise = (async () => {
    const manifest = await loadManifest(definition, definitionId, apiBaseUrl, getAccessToken, designerOrigin, environment);

    const icons = ensureReactRuntimeIconModules(manifest.runtime.iconModules ?? []);
    const stylesheet = manifest.stylesheet
      ? loadStylesheet(getArtifactUrl(apiBaseUrl, definitionId, definition.BuildId, manifest.stylesheet), getAccessToken, designerOrigin, environment)
      : Promise.resolve(removeInactiveStylesheets(undefined, environment?.root));
    const moduleUrl = getArtifactUrl(apiBaseUrl, definitionId, definition.BuildId, manifest.module);
    const moduleBytes = fetchReactArtifact(moduleUrl, getAccessToken, designerOrigin, environment?.fetch).then((response) => response.blob());
    const [, , blob] = await Promise.all([icons, stylesheet, moduleBytes]);
    const moduleBlobUrl = URL.createObjectURL(blob);
    try {
      return { module: (await import(/* webpackIgnore: true */ moduleBlobUrl)) as ReactModule, manifest };
    } finally {
      URL.revokeObjectURL(moduleBlobUrl);
    }
  })();
  setBounded(moduleByBuildId, cacheKey, promise);
  promise.catch(() => moduleByBuildId.delete(cacheKey));
  return promise;
}

type ReactBundleProviderProps = PropsWithChildren<{ definitionId?: string; definition?: ReactBundleModel }>;

const ReactBundleProvider: React.FC<ReactBundleProviderProps> = ({ definitionId, definition: reference, children }) => {
  const buildId = reference?.BuildId;
  // Designer drafts recreate reference objects; only a different immutable build should reload/remount code.
  const definition = useMemo(() => (buildId === undefined ? undefined : { BuildId: buildId }), [buildId]);
  const { API_URL, DESIGNER_ORIGIN, EMBED_FETCH, EMBED_STYLE_ROOT, EMBED_INSTANCE_ID, EMBED_MANIFEST } = useContext(ConfigContext);
  const environment = React.useMemo<ArtifactEnvironment>(
    () => ({ fetch: EMBED_FETCH, root: EMBED_STYLE_ROOT, instanceId: EMBED_INSTANCE_ID, manifest: EMBED_MANIFEST }),
    [EMBED_FETCH, EMBED_STYLE_ROOT, EMBED_INSTANCE_ID, EMBED_MANIFEST]
  );
  const { authService } = useContext(AuthContext);
  const [manifest, setManifest] = useState<ReactBundleManifest | null>(null);
  const [manifestFailed, setManifestFailed] = useState(false);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  useEffect(() => {
    let active = true;
    setManifest(null);
    setManifestFailed(false);
    setAuthenticationRequired(false);
    if (definition && definitionId) {
      loadManifest(definition, definitionId, API_URL, () => authService.getAccessToken(), DESIGNER_ORIGIN, environment)
        .then((loaded) => {
          if (active) setManifest(loaded);
        })
        .catch((error: unknown) => {
          if (!active) return;
          if (error instanceof ReactArtifactAuthenticationError) setAuthenticationRequired(true);
          else setManifestFailed(true);
        });
    }
    return () => {
      active = false;
    };
  }, [API_URL, DESIGNER_ORIGIN, authService, definition, definitionId, environment]);

  const componentStatus = useCallback(
    (componentName?: string, componentId?: string) => {
      if (!definition) return "unbound" as const;
      if (authenticationRequired) return "loading" as const;
      if (manifestFailed) return "bound" as const;
      if (!manifest) return "loading" as const;
      try {
        findDescriptor(manifest, definition.BuildId, componentName, componentId);
        return "bound" as const;
      } catch {
        return "unbound" as const;
      }
    },
    [authenticationRequired, definition, manifest, manifestFailed]
  );
  const loadBuild = useMemo(() => {
    let pending: Promise<LoadedReactBuild> | undefined;
    return () => {
      if (!definition || !definitionId) throw new Error("The App React bundle reference is missing.");
      return (pending ??= loadModule(definition, definitionId, API_URL, () => authService.getAccessToken(), DESIGNER_ORIGIN, environment));
    };
  }, [API_URL, DESIGNER_ORIGIN, authService, definition, definitionId, environment]);

  const loadComponent = useCallback(
    async (componentName?: string, componentId?: string) => {
      if (!definition) {
        throw new Error(`React component '${componentName}' cannot be loaded because this App has no React bundle.`);
      }
      if (!definitionId) {
        throw new Error(`React build '${definition.BuildId}' cannot be loaded because the App definition ID is missing.`);
      }
      const { module, manifest } = await loadBuild();
      const descriptor = findDescriptor(manifest, definition.BuildId, componentName, componentId);
      const component = module[descriptor.exportName];
      if (!component) {
        throw new Error(`React build '${definition.BuildId}' does not export '${descriptor.exportName}'.`);
      }

      return { default: component };
    },
    [definition, definitionId, loadBuild]
  );

  const value = useMemo(() => ({ buildId: definition?.BuildId, componentStatus, loadComponent }), [definition?.BuildId, componentStatus, loadComponent]);
  return <ReactBundleContext.Provider value={value}>{children}</ReactBundleContext.Provider>;
};

export default ReactBundleProvider;
export { ReactBundleContext };
